import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
} from '@ouropix/ui';
import { AlertCircle, Eye, EyeOff, Laptop, Loader2, Lock, MapPin, Pencil, Save, Shield, ShieldCheck, Trash2, User, X } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { changePassword } from '@/lib/auth';
import { Admin2FASetup } from '@/components/admin-ops/Admin2FASetup';
import { maskEmail } from '@ouropix/shared';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/api-helpers';
import { parseUA } from '@/lib/ua-parser';

export const Route = createFileRoute('/_admin/meu-perfil')({
  component: MeuPerfilPage,
});

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Superadmin',
  moderator: 'Moderador',
};

function MeuPerfilPage() {
  const session = useAuthStore((s) => s.session);
  const setSession = useAuthStore((s) => s.setSession);

  // Nome editável
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Senha
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changing, setChanging] = useState(false);

  // Dispositivos confiáveis
  type TrustedDevice = {
    id: string;
    device_info: { browser?: string; os?: string; userAgent?: string; [k: string]: unknown };
    ip_country: string | null;
    first_seen_at: string;
    last_seen_at: string;
    trusted_until: string | null;
    revoked: boolean;
    times_seen: number;
  };
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  async function loadDevices() {
    setDevicesLoading(true);
    try {
      const res = await api.get('/admin/me/trusted-devices');
      setDevices(res.data?.devices ?? []);
    } catch {
      // non-fatal — seção fica vazia
    } finally {
      setDevicesLoading(false);
    }
  }

  async function handleRevokeDevice(deviceId: string) {
    setRevoking(deviceId);
    try {
      await api.delete(`/admin/me/trusted-devices/${deviceId}`);
      toast.success('Dispositivo revogado');
      setDevices((prev) => prev.filter((d) => d.id !== deviceId));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Erro ao revogar dispositivo'));
    } finally {
      setRevoking(null);
    }
  }

  // Sincroniza fullName do servidor caso esteja vazio no store (ex: login 2FA antes do fix).
  useEffect(() => {
    if (session?.fullName) return;
    api.get('/auth/admin/profile').then((res) => {
      const name = res.data?.full_name;
      if (name && session) {
        setSession({ ...session, fullName: name });
      }
    }).catch(() => { /* non-fatal */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDevices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!session) return null;

  const maskedEmailValue = maskEmail(session.email);

  const passwordsMatch = newPassword === confirmPassword;
  const meetsRequirements =
    newPassword.length >= 8 &&
    /[A-Z]/.test(newPassword) &&
    /[a-z]/.test(newPassword) &&
    /\d/.test(newPassword);
  const canSubmit =
    currentPassword.length > 0 &&
    meetsRequirements &&
    passwordsMatch &&
    confirmPassword.length > 0 &&
    currentPassword !== newPassword;

  function handleStartEditName() {
    setNameValue(session!.fullName ?? '');
    setEditingName(true);
  }

  function handleCancelEditName() {
    setEditingName(false);
    setNameValue('');
  }

  async function handleSaveName() {
    const trimmed = nameValue.trim();
    if (!trimmed) return;
    setSavingName(true);
    try {
      await api.patch('/auth/admin/me', { full_name: trimmed });
      setSession({ ...session!, fullName: trimmed });
      toast.success('Nome atualizado!');
      setEditingName(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Erro ao atualizar nome'));
    } finally {
      setSavingName(false);
    }
  }

  async function handleChangePassword() {
    if (!canSubmit) return;
    setChanging(true);
    try {
      const result = await changePassword({ currentPassword, newPassword });
      if (!result.success) {
        toast.error(result.message || 'Erro ao atualizar senha');
        return;
      }
      toast.success('Senha atualizada com sucesso!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      toast.error('Erro ao atualizar senha');
    } finally {
      setChanging(false);
    }
  }

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <User className="h-6 w-6 text-primary" />
          Meu Perfil
        </h1>
        <p className="text-muted-foreground text-sm">
          Gerencie suas informações e segurança
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Informações do Perfil</CardTitle>
          <CardDescription>Seus dados no painel administrativo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            {/* Nome editável */}
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Nome</Label>
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    placeholder="Seu nome completo"
                    className="h-8 text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') handleCancelEditName();
                    }}
                  />
                  <Button
                    size="icon"
                    variant="default"
                    className="h-8 w-8 shrink-0"
                    onClick={handleSaveName}
                    disabled={savingName || !nameValue.trim()}
                  >
                    {savingName ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 shrink-0"
                    onClick={handleCancelEditName}
                    disabled={savingName}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="font-medium">{session.fullName || '—'}</p>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                    onClick={handleStartEditName}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Email</Label>
              <p className="font-medium">{maskedEmailValue || '—'}</p>
            </div>

            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Cargo</Label>
              <div>
                {session.role === 'superadmin' ? (
                  <Badge
                    variant="default"
                    className="bg-primary/20 text-primary border-primary/30"
                  >
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    {ROLE_LABELS[session.role]}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-muted">
                    <Shield className="h-3 w-3 mr-1" />
                    {ROLE_LABELS[session.role] || session.role}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Admin2FASetup />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Laptop className="h-5 w-5" />
            Dispositivos Confiáveis
          </CardTitle>
          <CardDescription>
            Dispositivos onde o 2FA foi dispensado por 30 dias. Revogue sessões suspeitas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {devicesLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando dispositivos...
            </div>
          ) : devices.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <AlertCircle className="h-4 w-4" />
              Nenhum dispositivo confiável ativo.
            </div>
          ) : (
            <div className="space-y-3">
              {devices.map((device) => {
                const isTrustedActive =
                  !device.revoked &&
                  !!device.trusted_until &&
                  new Date(device.trusted_until) > new Date();
                const parsed = parseUA(
                  typeof device.device_info?.userAgent === 'string' ? device.device_info.userAgent : null,
                );
                const browserInfo = device.device_info?.browser
                  ? String(device.device_info.browser)
                  : parsed.browser;
                const osInfo = device.device_info?.os ? String(device.device_info.os) : parsed.os;
                return (
                  <div
                    key={device.id}
                    className="flex items-start justify-between gap-3 rounded-lg border p-3"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <Laptop className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <div className="min-w-0 space-y-0.5">
                        <p className="text-sm font-medium truncate">{browserInfo}</p>
                        <p className="text-xs text-muted-foreground truncate">{osInfo}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {device.ip_country && (
                            <>
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span>{device.ip_country}</span>
                            </>
                          )}
                          <span>
                            Último acesso:{' '}
                            {new Date(device.last_seen_at).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        {isTrustedActive ? (
                          <p className="text-xs text-emerald-600 dark:text-emerald-400">
                            Confiável até{' '}
                            {new Date(device.trusted_until!).toLocaleDateString('pt-BR')}
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            {device.revoked ? 'Revogado' : 'Trust expirado'}
                          </p>
                        )}
                      </div>
                    </div>
                    {isTrustedActive && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleRevokeDevice(device.id)}
                        disabled={revoking === device.id}
                        title="Revogar dispositivo"
                      >
                        {revoking === device.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Alterar Senha
          </CardTitle>
          <CardDescription>Atualize sua senha de acesso ao painel</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Senha Atual</Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Digite sua senha atual"
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowCurrentPassword((v) => !v)}
                aria-label={showCurrentPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="newPassword">Nova Senha</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite a nova senha"
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowNewPassword((v) => !v)}
                aria-label={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showNewPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {newPassword.length > 0 && !meetsRequirements && (
              <p className="text-xs text-muted-foreground">
                Mínimo 8 caracteres, com maiúscula, minúscula e número.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repita a nova senha"
              autoComplete="new-password"
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-xs text-destructive">As senhas não coincidem</p>
            )}
          </div>

          <Button
            onClick={handleChangePassword}
            disabled={changing || !canSubmit}
            className="w-full"
          >
            {changing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Atualizando...
              </>
            ) : (
              'Atualizar Senha'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
